import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fbTrackRegistration } from '../lib/fbpixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AuthCallback handles the redirect from Emergent Google Auth.
 * It synchronously reads the session_id from the URL hash (#session_id=...),
 * exchanges it with our backend, persists the user, and redirects to /dashboard.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError('Missing session_id. Redirecting to home...');
      setTimeout(() => navigate('/', { replace: true }), 1500);
      return;
    }

    const exchange = async () => {
      try {
        const res = await axios.post(
          `${API}/auth/emergent-session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        const user = res.data?.user;
        if (user) {
          // FB Pixel: track new Google registrations (only if this is a new user)
          // Heuristic: if no prior user in localStorage with same id, treat as new
          try {
            const prev = JSON.parse(localStorage.getItem('user') || 'null');
            if (!prev || prev.principalId !== user.principalId) {
              fbTrackRegistration({ method: 'google' });
            }
          } catch (_e) { /* silent */ }

          localStorage.setItem('user', JSON.stringify(user));
          // Clean URL: remove the hash fragment
          window.history.replaceState(null, '', '/dashboard');
          navigate('/dashboard', { replace: true });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        console.error('Auth exchange failed:', err);
        setError(
          err.response?.data?.detail ||
            'Sign-in failed. Please try again from the home page.'
        );
        setTimeout(() => navigate('/', { replace: true }), 2200);
      }
    };

    exchange();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-lg max-w-sm w-full text-center">
        <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">
          {error ? 'Sign-in issue' : 'Signing you in...'}
        </h2>
        <p className="text-sm text-slate-500">
          {error || 'Securely completing your Google login. Please wait.'}
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
